import React, { useMemo, useState } from 'react';

import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Button } from '../../components/ui/Button/Button';
import {
    FilterBar,
    FilterReset,
    FilterSearch,
    FilterSelect,
} from '../../components/ui/FilterBar/FilterBar';
import type { FilterOption } from '../../components/ui/FilterBar/FilterBar';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { TableFooter } from '../../components/ui/TableFooter/TableFooter';
import { DataTable } from '../../components/ui/DataTable/DataTable';
import { SortableTh } from '../../components/ui/DataTable/SortableTh';
import { DateRangePicker, toIsoDate, type DateRange } from '../../components/ui/DatePicker';

import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useGetUsersRequestsQuery } from '../../entities/usersRequests/api/usersRequestsApi';
import type { UserRequestsRow } from '../../entities/usersRequests/model/types';
import { DEFAULT_PAGE_SIZE } from '../../shared/config/pagination';
import { formatRangeLabel } from '../../utils/format';
import { formatNumber } from '../../utils/number';
import styles from './RequestsPage.module.scss';

type Preset = 'prev' | 'cur' | '30' | '90' | 'year' | 'custom';
type Activity = 'all' | 'active' | 'idle';
type SortKey = 'name' | 'requests' | 'projects';

function presetRange(preset: Preset): DateRange | null {
    const now = new Date();

    switch (preset) {
        case 'prev': {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);

            return { start: toIsoDate(start), end: toIsoDate(end) };
        }
        case 'cur':
            return {
                start: toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
                end: toIsoDate(now),
            };
        case '30':
        case '90': {
            const start = new Date(now);
            start.setDate(start.getDate() - Number(preset));

            return { start: toIsoDate(start), end: toIsoDate(now) };
        }
        case 'year':
            return { start: toIsoDate(new Date(now.getFullYear(), 0, 1)), end: toIsoDate(now) };
        default:
            return null;
    }
}

/** Бэк ждёт дату в формате dd-mm-yyyy. */
function toApiDate(value: string): string {
    const [year, month, day] = value.split('-');

    return `${day}-${month}-${year}`;
}

function initials(name: string): string {
    const parts = name.split(' ').filter(Boolean);

    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '—';
}

const PRESET_OPTIONS: FilterOption<Preset>[] = [
    { value: 'prev', label: 'Прошлый месяц' },
    { value: 'cur', label: 'Текущий месяц' },
    { value: '30', label: 'Последние 30 дней' },
    { value: '90', label: 'Последние 90 дней' },
    { value: 'year', label: 'С начала года' },
    { value: 'custom', label: 'Произвольный' },
];

const ACTIVITY_OPTIONS: FilterOption<Activity>[] = [
    { value: 'all', label: 'Все пользователи' },
    { value: 'active', label: 'Только активные' },
    { value: 'idle', label: 'Без запросов' },
];

const INITIAL_RANGE = presetRange('prev')!;

export function RequestsPage() {
    const [preset, setPreset] = useState<Preset>('prev');
    const [range, setRange] = useState<DateRange>(INITIAL_RANGE);
    const [search, setSearch] = useState('');
    const [activity, setActivity] = useState<Activity>('all');
    const [sortKey, setSortKey] = useState<SortKey>('requests');
    const [dir, setDir] = useState<-1 | 1>(-1);
    const [page, setPage] = useState(1);

    /* Поиск уходит на сервер параметром query — без задержки каждый символ
       превращался бы в отдельный запрос. */
    const query = useDebouncedValue(search, 400);

    const { data, isLoading, isFetching, isError, refetch } = useGetUsersRequestsQuery({
        since: toApiDate(range.start),
        till: toApiDate(range.end),
        query,
    });

    const rows = useMemo(() => data?.rows ?? [], [data]);
    const summary = data?.summary;

    const changeSearch = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    const changePreset = (value: Preset) => {
        setPreset(value);

        const next = presetRange(value);
        if (next) setRange(next);

        setPage(1);
    };

    /* Ручной выбор в календаре переводит пресет в «Произвольный». */
    const changeRange = (next: DateRange) => {
        setRange(next);
        setPreset('custom');
        setPage(1);
    };

    const changeActivity = (value: Activity) => {
        setActivity(value);
        setPage(1);
    };

    const reset = () => {
        setSearch('');
        setActivity('all');
        setSortKey('requests');
        setDir(-1);
        changePreset('prev');
    };

    const isFiltered = search !== '' || activity !== 'all' || preset !== 'prev';

    /* Сортировка и фильтр активности — на клиенте: ручка отдаёт всех
       пользователей сразу, дробить это на запросы незачем. */
    const visible = useMemo(() => {
        const filtered = rows.filter((row) => {
            if (activity === 'active' && row.totalCount === 0) return false;
            if (activity === 'idle' && row.totalCount > 0) return false;

            return true;
        });

        const compare = (a: UserRequestsRow, b: UserRequestsRow) => {
            if (sortKey === 'requests') return a.totalCount - b.totalCount;
            if (sortKey === 'projects') return a.projectsCount - b.projectsCount;

            return a.fullName.localeCompare(b.fullName, 'ru');
        };

        return [...filtered].sort((a, b) => compare(a, b) * dir);
    }, [rows, activity, sortKey, dir]);

    const totalRequests = useMemo(
        () => visible.reduce((sum, row) => sum + row.totalCount, 0),
        [visible],
    );

    const pageCount = Math.max(1, Math.ceil(visible.length / DEFAULT_PAGE_SIZE));
    const safePage = Math.min(page, pageCount);
    const offset = (safePage - 1) * DEFAULT_PAGE_SIZE;
    const slice = visible.slice(offset, offset + DEFAULT_PAGE_SIZE);

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setDir((prev) => (prev === 1 ? -1 : 1));
        } else {
            setSortKey(key);
            setDir(key === 'name' ? 1 : -1);
        }
    };

    const sortDir = dir === 1 ? 'asc' : 'desc';

    const activeUsers = summary?.activeUsers ?? 0;
    const avgPerActive = activeUsers > 0 ? Math.round((summary?.requests ?? 0) / activeUsers) : 0;

    const emptyState = (() => {
        if (isError) {
            return (
                <EmptyState
                    title="Не удалось загрузить отчёт"
                    description="Проверьте соединение и попробуйте ещё раз."
                    action={{ label: 'Повторить', onClick: refetch }}
                />
            );
        }

        if (slice.length === 0) {
            return (
                <EmptyState
                    title="За период запросов нет"
                    description="Ни один пользователь не обращался к системе в выбранном интервале."
                    action={isFiltered ? { label: 'Сбросить фильтры', onClick: reset } : undefined}
                />
            );
        }

        return undefined;
    })();

    return (
        <div className={styles.page}>
            <PageHeader
                title="Информация по лимитам"
                actions={
                    <Button onClick={() => refetch()} disabled={isFetching}>
                        Обновить
                    </Button>
                }
            />

            <FilterBar>
                <FilterSearch
                    value={search}
                    placeholder="Пользователь или организация"
                    onChange={changeSearch}
                />

                <FilterReset active={isFiltered} onClick={reset} />

                <FilterSelect
                    options={PRESET_OPTIONS}
                    value={preset}
                    allValue="prev"
                    onChange={changePreset}
                />

                <DateRangePicker value={range} onChange={(next) => next && changeRange(next)} />

                <FilterSelect
                    options={ACTIVITY_OPTIONS}
                    value={activity}
                    allValue="all"
                    onChange={changeActivity}
                    align="right"
                />
            </FilterBar>

            <DataTable
                static
                scrollKey={`${safePage}-${activity}-${sortKey}-${dir}-${query}-${range.start}-${range.end}`}
                loading={isFetching}
                isLoading={isLoading}
                loadingText="Загружаем отчёт…"
                empty={emptyState}
                footer={
                    visible.length > 0 ? (
                        <TableFooter
                            page={safePage}
                            totalPages={pageCount}
                            rangeLabel={formatRangeLabel(safePage, DEFAULT_PAGE_SIZE, visible.length)}
                            onPageChange={setPage}
                        />
                    ) : undefined
                }
            >
                <thead>
                    <tr>
                        <SortableTh
                            title="Пользователь"
                            active={sortKey === 'name'}
                            dir={sortDir}
                            onClick={() => toggleSort('name')}
                        />

                        <th scope="col">Организация</th>

                        <SortableTh
                            title="Запросов"
                            active={sortKey === 'requests'}
                            dir={sortDir}
                            align="right"
                            onClick={() => toggleSort('requests')}
                        />

                        <SortableTh
                            title="Проектов"
                            active={sortKey === 'projects'}
                            dir={sortDir}
                            align="right"
                            onClick={() => toggleSort('projects')}
                        />
                    </tr>
                </thead>

                <tbody>
                    {slice.map((row) => {
                        const share = totalRequests
                            ? Math.round((row.totalCount / totalRequests) * 1000) / 10
                            : 0;

                        return (
                            <tr key={row.id}>
                                <td>
                                    <div className={styles.user}>
                                        <span className={styles.ava}>{initials(row.fullName)}</span>
                                        <span className={styles.reportTitle}>{row.fullName}</span>
                                    </div>
                                </td>

                                <td className={styles.muted}>{row.organization}</td>

                                <td className={styles.cellNums}>
                                    {row.totalCount > 0 ? (
                                        <>
                                            {formatNumber(row.totalCount)}
                                            <span className={styles.subText}>
                                                {share.toFixed(1).replace('.', ',')} %
                                            </span>
                                        </>
                                    ) : (
                                        <span className={styles.dim}>0</span>
                                    )}
                                </td>

                                <td className={styles.cellNums}>
                                    <span className={row.projectsCount ? undefined : styles.dim}>
                                        {formatNumber(row.projectsCount)}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </DataTable>

            <div className={styles.summary}>
                <div className={styles.sum}>
                    <span>Пользователей</span>
                    <b>{formatNumber(summary?.users ?? 0)}</b>
                    <i>активных {formatNumber(activeUsers)}</i>
                </div>
                <div className={styles.sum}>
                    <span>Запросов</span>
                    <b>{formatNumber(summary?.requests ?? 0)}</b>
                    <i>в среднем {formatNumber(avgPerActive)} на активного</i>
                </div>
                <div className={styles.sum}>
                    <span>Проектов</span>
                    <b>{formatNumber(summary?.projects ?? 0)}</b>
                </div>
                <div className={styles.sum}>
                    <span>Товаров</span>
                    <b>{formatNumber(summary?.products ?? 0)}</b>
                </div>
            </div>
        </div>
    );
}
